import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { Button } from 'components/Button/Button';
import { SnapshotCreationAddDevices } from 'components/SnapshotCreation/SnapshotCreationAddDevices';
import { SnapshotCreationNextStep } from 'components/SnapshotCreation/SnapshotCreationNextStep';
import { SnapshotCreationTable } from 'components/SnapshotCreation/SnapshotCreationTable';
import { useSnapshotCreation } from 'components/SnapshotCreation/useSnapshotCreation';
import { useLocale } from 'containers/App/LocaleContext';
import { memo, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { SettingsPage } from '../SettingsPage';
import { useSnapshots } from '../useSnapshots';

const BareSnapshotInventory: FC = () => {
  const snapshotId = useSnapshotId();
  const snapshots = useSnapshots();
  const msg = useLocale();
  const {
    snapshotManagement: { snapshotCreation: snapshotCreationMsg },
  } = msg;

  const currentSnapshot = useMemo(
    () => snapshots.find((snapshot) => snapshot.id === snapshotId),
    [snapshots, snapshotId],
  );

  const snapshotCreation = useSnapshotCreation({
    defaultTabId: 'refreshDevices',
    preselectedSns: [],
    snapshotId,
  });

  const {
    breadcrumbsStep,
    failedData,
    insertNetworkFormik,
    loadingVendorsState,
    onBreadcrumbsStep1Click,
    onContinueDiscovery,
    onFetchVendors,
    onInsertNetwork,
    onSnapshotSettingsClose,
    onSnapshotSettingsOpen,
    onSourceSelect,
    onTabChange,
    requestErrors,
    selectedSns,
    sendingDataState,
    snapshotSettingsTab,
    tabId,
    taskApiSourceMap,
    vendors,
    setVendors,
  } = snapshotCreation;

  const getAddDevicesStartDiscoveryButton = (): ReactNode | null => {
    const disabledAddDevicesButton =
      !insertNetworkFormik.isValid || sendingDataState === 'LOADING';
    const starting = sendingDataState === 'LOADING';
    const showSpinner = starting;
    return (
      <Button
        accent='positive'
        variant='primary'
        disabled={disabledAddDevicesButton}
        loading={showSpinner}
        onClick={() => onInsertNetwork()}
        size='xs'
        type='button'
      >
        {starting
          ? msg.snapshotCreation.waitStarting
          : snapshotCreationMsg.startDiscoveryButton}
      </Button>
    );
  };

  const getContent = () => {
    switch (tabId) {
      case 'addDevices':
        return (
          <SnapshotCreationAddDevices
            insertNetworkFormik={insertNetworkFormik}
            loadingVendorsState={loadingVendorsState}
            onFetchVendors={() => onFetchVendors()}
            vendors={vendors}
            setVendors={setVendors}
          />
        );
      default:
        return (
          <SnapshotCreationTable
            canCreateNewSnapshot={false}
            preselectedSns={[]}
            snapshotCreation={snapshotCreation}
          />
        );
    }
  };

  const handleSettingsSave = useStableCallback(() => {
    onContinueDiscovery({ notSelectedDeviceSns: [] });
  });

  if (snapshotId === null) {
    return null;
  }

  return (
    <>
      <SettingsPage
        foundSnapshot={currentSnapshot}
        newSnapshot={false}
        onClose={onSnapshotSettingsClose}
        onSettingsSaved={handleSettingsSave}
        showSettings={snapshotSettingsTab !== null}
      />
      {breadcrumbsStep === 1 && (
        <>
          <div className='mb-6 flex items-center gap-2'>
            {tabId === 'addDevices' ? (
              <>
                <div className='mr-4 text-xl font-semibold'>
                  {msg.snapshotCreation.addDevices.heading}
                </div>
                <Button
                  variant='secondary'
                  onClick={() => {
                    // go back to any tab that is not addDevices; Tabs are removed, but
                    // keeping the logic for now since workflow logic in useSnapshotCreation
                    // is bound to active tab too.
                    onTabChange('refreshDevices');
                  }}
                  size='xs'
                  type='button'
                >
                  {msg.snapshotCreation.addDevices.btnCancel}
                </Button>
                {getAddDevicesStartDiscoveryButton()}
              </>
            ) : (
              <Button
                variant='secondary'
                onClick={() => {
                  onTabChange('addDevices');
                }}
                size='xs'
                type='button'
                icon='fa-plus'
              >
                {msg.snapshotCreation.addDevices.btnToggleAddDevices}
              </Button>
            )}
          </div>
          {getContent()}
        </>
      )}
      {requestErrors !== null && (
        <SnapshotCreationNextStep
          breadcrumbsStep={breadcrumbsStep}
          onBreadcrumbsStep1Click={onBreadcrumbsStep1Click}
          failedData={failedData}
          onContinueDiscovery={onContinueDiscovery}
          onSnapshotSettingsOpen={onSnapshotSettingsOpen}
          onSourceSelect={onSourceSelect}
          requestErrors={requestErrors}
          selectedSns={selectedSns}
          sendingDataState={sendingDataState}
          taskApiSourceMap={taskApiSourceMap}
        />
      )}
    </>
  );
};

export const SnapshotInventory = memo(BareSnapshotInventory);
